import type { ReactNode } from 'react';
import type { Shop } from '@features/shop/types';

import { useParams } from 'react-router';
import { useMemo, useContext, createContext } from 'react';

import { FatalError } from '@shared/components/fatal-error';

import { useUser } from '@features/user-access';

type ShopContextType = {
  shop: Shop;
};

// 1. Créer le Context avec une valeur par défaut de `null`
//    C'est normal à ce niveau, car en dehors du Provider, il n'y a pas de données.
const ShopContext = createContext<ShopContextType | null>(null);

// 2. Créer le Provider
export const ShopProvider = ({ children }: { children: ReactNode }) => {
  const {
    userAccess: { shops },
  } = useUser();

  const { shopSlug } = useParams();
  const shop = shops!.find((shop) => shop.slug === shopSlug);

  const value = useMemo(
    () => ({
      // On doit s'assurer que companySettings est défini ici
      // Mais comme on le vérifie juste après, on peut utiliser un "non-null assertion" (!)
      // ou le laisser potentiellement undefined dans la memo, la vérification ci-dessous fera le travail.
      shop: shop!,
    }),
    [shop]
  );

  // En cas d'erreur critique qui empêche l'app de fonctionner
  if (!shop) {
    // Vous pouvez logguer l'erreur `error` ici
    return <FatalError />;
  }

  // 3. Si tout va bien, on fournit les données via le Provider
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

type UseShopType = {
  shop: Shop;
};

// 4. Créer le hook final `useShop`
export const useShop = (): UseShopType => {
  const context = useContext(ShopContext);

  // Le hook lèvera une erreur s'il est utilisé hors du Provider,
  // ce qui garantit que la valeur n'est jamais `null` là où on l'utilise.
  if (context === null) {
    throw new Error("useShop() doit être utilisé à l'intérieur d'un ShopProvider");
  }

  return {
    ...context,
  };
};
